import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import EditBundle from './EditBundle';
import { fetchBundle, fetchPractitionerByGsdlNo, fetchPractitionerOrderableItems } from '../../util/apiUtils';
import ReturnToOrder from '../ReturnToOrder';

/**
 * Loads a client's OrderableItem list and appropriate saved bundle details and then displays the
 * components for managing bundles.
 * The client and bundle Ids are retrieved from URL params.
 */
const ManageBundles = () => {
	let { gsdlNo: gsdlNoParam, bundleId } = useParams();
	const [loading, setLoading] = useState(true);
	const [orderableItems, setOrderableItems] = useState(null);
	const [bundle, setBundle] = useState(null);
	const [error, setError] = useState(false);

	// Reset to an empty bundle if the passed in client ID changes
	useEffect(() => {
		setBundle({
			gsdlNo: gsdlNoParam,
			products: []
		});
	}, [gsdlNoParam]);

	/**
	 * Fetch a new OrderableItems list and practitioner details when the client changes.
	 * This will happen if the client ID is passed in as a parameter or if the client ID is
	 * determined after fetching a saved bundle.
	 */
	const gsdlNo = bundle?.gsdlNo;
	useEffect(() => {
		if (gsdlNo) {
			setLoading(true);
			setError(false);

			const orderableItemsPromise = fetchPractitionerOrderableItems(gsdlNo);
			const practitionerPromise = fetchPractitionerByGsdlNo(gsdlNo);

			Promise.all([orderableItemsPromise, practitionerPromise])
				.then(([orderableItems, practitioner]) => {
					setOrderableItems(orderableItems);
					setBundle((prevBundle) => ({
						...prevBundle,
						practitioner: practitioner
					}))
				})
				.catch((e) => {
					setError(true);
				})
				.finally(() => setLoading(false));
		}
	}, [gsdlNo]);

	// Fetch the details of an existing bundle
	useEffect(() => {
		if (!!bundleId) {
			setLoading(true);
			setError(false);

			fetchBundle(bundleId)
				.then((bundle) => {
					if (!bundle) {
						setError(true);
						return;
					}
					/**
					 * OrderableItemList code expects components and addons to be a list of Strings
					 *  but the returned bundle has a list of numbers.
					 * It also expects each product to have a quantity.
					 * Convert the lists to the expected format and add a quantity.
					 **/
					setBundle({
						...bundle,
						products:
							bundle?.products?.map((bundleProduct) => {
								return {
									...bundleProduct,
									quantity: 1,
									components: bundleProduct?.components?.map(String) || [],
									addons: bundleProduct?.addons?.map(String) || []
								};
							}) || []
					});
				})
				.catch((err) => setError(true))
				.finally(() => setLoading(false));
		}
	}, [bundleId]);

	return (
		<>
			{/* Can't easily use Breadcrumb component because we need 'offset-lg-1' for alignment to match */}
			<div className="gear-page-intro">
				<div className="container-fluid">
					<div className="row">
						<div className="col-sm-8 col-md-7 col-lg-6 offset-lg-1">
							<ol className="breadcrumb">
								<li>
									<i className="fa fa-home pe-2"></i>
									<a href="/mygdx">myGDX Home</a>
								</li>
								<li>
									<Link to={'/'}> Activate & Order Tests </Link>
								</li>
								<li className="active"> Manage Custom Profiles</li>
							</ol>
						</div>
						<div className="col-sm-4 col-md-5 col-lg-3 text-right mt-10">
							<ReturnToOrder />
						</div>
					</div>
				</div>
			</div>

			<div className="container-fluid">
				{error ? (
					<div className="row">
						<div className="col-lg-11 offset-lg-1">
							<h3 className="text-danger">An unexpected error occurred while loading your custom profiles</h3>
						</div>
					</div>
				) : (
					<>
						{loading || !orderableItems ? (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									margin: '40px 40px 0 40px'
								}}
							>
								<h3 className="mt-0 mb-20">Loading Custom Profiles</h3>
								<i className="fa fa-spinner fa-spin fa-fw" style={{ fontSize: '5em' }}></i>
							</div>
						) : (
							<EditBundle orderableItems={orderableItems} bundle={bundle} updateBundle={setBundle} />
						)}
					</>
				)}
			</div>
		</>
	);
};
export default ManageBundles;
